import React, { useState } from 'react';
import { PolicyRevision, Company } from '../../types';
import { FileText, Copy, Check, ExternalLink, Calendar, Hash, Shield, Search, ArrowLeft } from 'lucide-react';

interface DocumentReaderProps {
  revision: PolicyRevision;
  company: Company;
  policyType: string;
  onBack?: () => void;
  onCompareDiff?: () => void;
}

export const DocumentReader: React.FC<DocumentReaderProps> = ({
  revision,
  company,
  policyType,
  onBack,
  onCompareDiff,
}) => {
  const [copied, setCopied] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const rawText = revision.rawText || revision.raw_text || revision.textExcerpt || revision.text_excerpt || 'No text content available for this revision.';
  const title = revision.title || `${company.name} ${policyType.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}`;
  const effectiveDate = revision.effectiveDate || revision.effective_date;
  const scrapedDate = revision.scrapedAt || revision.scraped_at;
  const wordCount = revision.wordCount || revision.word_count || rawText.split(/\s+/).filter(Boolean).length;
  const sha256 = revision.sha256Hash || revision.sha256_hash;
  const keyTakeaways = revision.keyTakeaways || revision.key_takeaways || [];
  const summary = revision.executiveSummary || revision.executive_summary;

  const originalUrl = company.trackedPolicies?.[policyType]?.url || company.tracked_policies?.[policyType]?.url;

  const handleCopy = () => {
    navigator.clipboard.writeText(rawText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple paragraph formatting
  const paragraphs = rawText.split(/\n\n+/).filter(Boolean);

  const filteredParagraphs = searchQuery.trim()
    ? paragraphs.filter((p) => p.toLowerCase().includes(searchQuery.toLowerCase()))
    : paragraphs;

  return (
    <div className="space-y-6">
      {/* Top Document Header Bar */}
      <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-2xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-1.5 -ml-1 rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  title="Back to Timeline"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold">
                {company.name}
              </span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                {policyType.replace('_', ' ')}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {title}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-2xs"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy Text</span>
                </>
              )}
            </button>

            {originalUrl && (
              <a
                href={originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Original URL</span>
              </a>
            )}

            {onCompareDiff && (
              <button
                onClick={onCompareDiff}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-2xs"
              >
                <span>View Diffs</span>
              </button>
            )}
          </div>
        </div>

        {/* Metadata stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-slate-100 dark:border-slate-700/60 text-xs">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Calendar className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Effective Date</span>
              <span className="font-semibold">{effectiveDate ? new Date(effectiveDate).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <FileText className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Word Count</span>
              <span className="font-semibold">{wordCount.toLocaleString()} words</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Shield className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Revision ID</span>
              <span className="font-semibold font-mono text-[11px] truncate max-w-[120px] block">{revision.id}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Hash className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">SHA-256 Fingerprint</span>
              <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[120px] block">
                {sha256 ? `${sha256.substring(0, 10)}...` : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* AI Key Takeaways (if present) */}
      {(summary || keyTakeaways.length > 0) && (
        <div className="bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <h3 className="font-bold text-xs uppercase tracking-wider text-indigo-900 dark:text-indigo-200">
              AI Summary & Key Takeaways
            </h3>
          </div>
          {summary && (
            <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
              {summary}
            </p>
          )}
          {keyTakeaways.length > 0 && (
            <ul className="space-y-1.5 pt-1">
              {keyTakeaways.map((point, idx) => (
                <li key={idx} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-2">
                  <span className="text-indigo-500 font-bold">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Search within document */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter document sections..."
          className="w-full pl-9 pr-4 py-2.5 text-xs rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-2xs"
        />
      </div>

      {/* Full Document Prose Body */}
      <div className="bg-white dark:bg-slate-800 p-8 sm:p-12 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-2xs">
        <article className="max-w-none space-y-4 text-slate-850 dark:text-slate-200 leading-relaxed text-sm font-normal">
          {filteredParagraphs.map((para, idx) => {
            // If paragraph looks like a heading
            if (para.startsWith('#') || (para.length < 90 && para === para.toUpperCase() && para.length > 3)) {
              return (
                <h2
                  key={idx}
                  className="text-base sm:text-lg font-bold text-slate-900 dark:text-white pt-4 pb-1 border-b border-slate-100 dark:border-slate-700/40"
                >
                  {para.replace(/^#+\s*/, '')}
                </h2>
              );
            }

            return (
              <p key={idx} className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                {para}
              </p>
            );
          })}
        </article>
      </div>
    </div>
  );
};
